import React, { useEffect, useState, useCallback } from "react"; // Added useCallback
import * as api from "../../api/index";
import { jwtDecode } from "jwt-decode";
import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { FiBell, FiCheckCircle, FiInbox, FiClock, FiCircle } from "react-icons/fi";

const NotificationsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  let userId = null;
  try {
    const decoded = jwtDecode(token);
    userId = decoded.id || decoded._id;
  } catch (e) {
    // silence error
  }

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // --- ADDED PAGINATION STATE (Strictly Additive) ---
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // --- UPDATED FETCH LOGIC FOR 20 ITEMS ---
  const fetchNotifications = useCallback(async (pageNum = 1, shouldRefresh = false) => {
    if (!userId) {
      setError("User not authenticated.");
      setNotifications([]);
      setLoading(false);
      return;
    }

    if (pageNum > 1 && !hasMore && !shouldRefresh) return;

    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      
      setError("");

      // Fetching with page and limit 20
      const res = await api.getNotifications(userId, pageNum, 20);
      const newNotifs = res.data.notifications || [];

      if (pageNum === 1 || shouldRefresh) {
        setNotifications(newNotifs);
        setHasMore(newNotifs.length === 20);
        setPage(1);
      } else {
        setNotifications((prev) => [...prev, ...newNotifs]);
        setHasMore(newNotifs.length === 20);
      }
    } catch (err) {
      setError("Failed to fetch notifications.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, hasMore]);

  // --- ADDED LOAD MORE HANDLER ---
  const handleLoadMore = () => {
    if (!loading && !loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNotifications(nextPage);
    }
  };

  useEffect(() => {
    fetchNotifications(1, true);
  }, [userId]);

  const markAsRead = async (id) => {
    try {
      if (userId) {
        await api.markNotificationRead(id);
      }
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      setError("Failed to update notification");
    }
  };

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="bg-gray-50 min-h-screen pb-12">
      {/* 🔝 Sticky Header - UNCHANGED */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition text-gray-700 flex items-center gap-1"
        >
          <IoMdArrowBack size={24} />
          <span className="text-[10px] font-black uppercase tracking-widest">Back</span>
        </button>
        <h1 className="text-lg font-black text-gray-900 tracking-tight pr-10">Inbox</h1>
        <div />
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        {/* 🔔 Notification Header Card - UNCHANGED */}
        <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center">
                <FiBell size={24} />
              </div>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-orange-600 border-4 border-white rounded-full"></span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-black text-gray-900">Notifications</h2>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                {unreadCount} Unread Messages
              </p>
            </div>
          </div>
          <button 
            onClick={() => fetchNotifications(1, true)}
            className="p-3 bg-gray-50 hover:bg-gray-100 rounded-2xl transition text-gray-400 hover:text-gray-900"
          >
            <FiClock size={20} />
          </button>
        </div>

        {/* 📋 Notifications List - STYLES UNCHANGED */}
        <div className="space-y-4">
          {loading && notifications.length === 0 ? (
            <div className="flex flex-col items-center py-20 gap-4">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-orange-600 rounded-full animate-spin"></div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fetching Updates...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 p-6 rounded-[2rem] border border-red-100 text-center">
              <p className="text-red-600 font-black text-xs uppercase">{error}</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center py-20 opacity-30">
              <FiInbox size={64} className="mb-4 text-gray-400" />
              <p className="font-black text-gray-400 uppercase tracking-widest text-sm">All caught up!</p>
            </div>
          ) : (
            <>
              {notifications.map((n) => (
                <div
                  key={n._id}
                  onClick={() => markAsRead(n._id)}
                  className={`group relative p-6 rounded-[2rem] transition-all cursor-pointer border shadow-sm active:scale-[0.98] ${
                    n.isRead 
                      ? "bg-white border-gray-100 opacity-70" 
                      : "bg-white border-teal-100 ring-2 ring-teal-50"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      {n.isRead ? (
                        <FiCheckCircle className="text-gray-300" size={20} />
                      ) : (
                        <FiCircle className="text-teal-500 fill-teal-500 animate-pulse" size={20} />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className={`font-black text-sm uppercase tracking-tight ${n.isRead ? "text-gray-500" : "text-gray-900"}`}>
                          {n.title}
                        </h3>
                        <span className="text-[9px] font-black text-gray-300 uppercase shrink-0 ml-2">
                          {new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className={`text-xs font-medium leading-relaxed ${n.isRead ? "text-gray-400" : "text-gray-600"}`}>
                        {n.message}
                      </p>
                      <p className="text-[9px] font-black text-gray-300 mt-3 uppercase tracking-widest">
                        {new Date(n.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* 🔄 ADDED LOAD MORE BUTTON (Strictly Additive) */}
              {hasMore && (
                <div className="flex justify-center pt-8">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="text-[10px] font-black text-orange-600 uppercase tracking-widest border-2 border-orange-100 px-8 py-3 rounded-full hover:bg-orange-50 transition-all disabled:opacity-50"
                  >
                    {loadingMore ? "Loading..." : "Load More Notifications"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <p className="text-center mt-12 text-[10px] font-black text-gray-300 uppercase tracking-widest leading-loose">
          Secure End-to-End Updates • OurASAP
        </p>
      </div>
    </div>
  );
};

export default NotificationsPage;